<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-4xl mx-auto px-4 py-10 w-full">

      <h1 class="text-3xl font-bold mb-8 text-center">
        Perfil do Freelancer
      </h1>

      <div class="bg-white shadow-xl rounded-2xl p-6 md:p-10 space-y-8">

        <!-- FOTO -->
        <div class="flex justify-center">
          <img v-if="previewImage" :src="previewImage" class="w-32 h-32 rounded-full object-cover shadow-lg border" />
          <div v-else class="w-32 h-32 rounded-full bg-gray-300"></div>
        </div>

        <!-- DADOS DO FREELANCER -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <div>
            <label class="font-medium">Nome</label>
            <p class="mt-1 bg-gray-100 p-3 rounded">{{ form.name }}</p>
          </div>

          <div>
            <label class="font-medium">Headline</label>
            <p class="mt-1 bg-gray-100 p-3 rounded">{{ form.headline }}</p>
          </div>

          <div class="md:col-span-2">
            <label class="font-medium">Bio</label>
            <p class="mt-1 bg-gray-100 p-3 rounded whitespace-pre-line">
              {{ form.bio }}
            </p>
          </div>

          <div>
            <label class="font-medium">Nível</label>
            <p class="mt-1 bg-gray-100 p-3 rounded capitalize">
              {{ form.experience_level }}
            </p>
          </div>

          <div>
            <label class="font-medium">Experiência Profissional</label>
            <p class="mt-1 bg-gray-100 p-3 rounded whitespace-pre-line">
              {{ form.professional_experience }}
            </p>
          </div>

          <!-- SKILLS -->
          <div class="md:col-span-2">
            <label class="font-medium">Habilidades</label>

            <div class="flex flex-wrap gap-2 mt-3">
              <div v-for="(skill, index) in form.skills" :key="index"
                class="bg-[#1a1a1a] text-white px-3 py-1 rounded-full text-sm">
                {{ skill.description }}
              </div>
            </div>
          </div>
        </div>

        <!-- REVIEWS -->
        <div class="mt-10">
          <h2 class="text-2xl font-bold mb-4">Avaliações</h2>

          <div v-if="reviews.length === 0" class="text-gray-500">
            Nenhuma avaliação até o momento.
          </div>

          <div v-for="review in reviews" :key="review.id" class="border p-4 rounded-lg mb-4 bg-gray-50">
            <div class="flex justify-between items-center mb-2">
              <p class="font-semibold">Projeto: {{ review.project_title }}</p>

              <!-- ⭐ ESTRELAS -->
              <div class="flex items-center gap-1">
                <span v-for="n in 5" :key="n" class="text-xl"
                  :class="n <= review.rating ? 'text-yellow-500' : 'text-gray-300'">
                  ★
                </span>
              </div>
            </div>

            <p class="text-gray-700 whitespace-pre-line">
              {{ review.comment }}
            </p>

            <p class="text-sm text-gray-400 mt-2">
              {{ new Date(review.created_at).toLocaleDateString() }}
            </p>
          </div>
        </div>

      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";

const route = useRoute();

const form = ref({
  id: "",
  name: "",
  headline: "",
  bio: "",
  experience_level: "",
  professional_experience: "",
  skills: [],
});

const previewImage = ref(null);
const reviews = ref([]);

function buildStorageUrl(rawPath) {
  if (!rawPath) return null;

  const maybe = String(rawPath);

  if (/^(https?:)?\/\//i.test(maybe)) return maybe;

  const base = (process.env.VUE_APP_URL_API_STORAGE || "").replace(/\/+$/, "");
  const normalized = maybe.replace(/\\/g, "/").replace(/^\/+/, "");

  return `${base}/${normalized}`;
}

function resolveAttachmentPreview(attachmentField) {
  if (!attachmentField) return null;
  if (typeof attachmentField === "object" && attachmentField.url)
    return buildStorageUrl(attachmentField.url);
  if (Array.isArray(attachmentField)) return buildStorageUrl(attachmentField[0]);
  if (typeof attachmentField === "string") return buildStorageUrl(attachmentField);
  return null;
}

onMounted(async () => {
  const { hash, reference } = route.params;

  const { data } = await api.get(`/users/freelancer/view`, {
    params: { hash, reference },
  });

  const u = data.data;

  form.value = {
    id: u.id,
    name: u.name,
    headline: u.headline,
    bio: u.bio,
    experience_level: u.experience_level,
    professional_experience: u.professional_experience,
    skills: Array.isArray(u.skills)
      ? u.skills.map((s) =>
        typeof s === "string" ? { id: null, description: s } : s
      )
      : [],
  };

  previewImage.value = resolveAttachmentPreview(u.attachment);

  reviews.value = u.reviewsReceived || [];
});
</script>
